import React, { useEffect, useRef, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Button, Divider, Tooltip } from 'antd';
import { SubPanelType } from '@/pages/Active/Active';
import { AccountInfo } from '@/components/AccountInfo/AccountInfo';
import { MessageIcon } from '@/components/MessageIcon/MessageIcon';
import { IEntity } from '@/shared/interfaces/entity.interface';
import muteIcon from '@/public/icons/mute.svg';
import pauseRecordingIcon from '@/public/icons/pauseRecording.svg';
import unPauseRecordingIcon from '@/public/icons/unPauseRecording.svg';
import unmuteIcon from '@/public/icons/unmute.svg';
import holdIcon from '@/public/icons/hold.svg';
import unHoldIcon from '@/public/icons/unhold.svg';
import transferIcon from '@/public/icons/transfer-filled.svg';
import dialPadIcon from '@/public/icons/dial-pad.svg';
import dialPadIconClicked from '@/public/icons/dial-pad-clicked.svg';
import groupCallIcon from '@/public/icons/group-call.svg';
import groupCallClickedIcon from '@/public/icons/group-call-clicked.svg';
import declineIcon from '@/public/icons/decline.svg';
import transferClickedIcon from '@/public/icons/transfer-clicked.svg';
import { getRegionByPhoneNumber } from '@/services/phoneRegionLocator/getRegionByPhoneNumber';
import { Dynamics } from '@/services/dynamics/dynamics';
import { useStore } from '@/components/../stores';
import { COMMON } from '@/components/../configs/main.config';
import { ISession, IStream, SipJs } from '@/services/sip-js.service';
import { getExtensionFromUri } from '@/shared/helpers/get-from-uri.helper';
import { PAUSE_RECORDING_FEATURE_FLAG } from '@/shared/constants/callPage';
import { USER_TYPE } from '@/shared/constants/user';
import { State } from '@/services/state.service';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import './Player.scss';

interface PlayerProps {
  session: ISession;
  onSwitchSubPanel: (value: SubPanelType) => void;
}

export const Player = observer(({ session, onSwitchSubPanel }: PlayerProps) => {
  const getUser = (): IEntity =>
    session.remoteUser.connectedUsers.length
      ? (session.remoteUser.connectedUsers[0] as IEntity)
      : {
          ...COMMON.UNKNOWN_ENTITY,
        };

  const queueName = SipJs.getSessionByType('active')?.remoteUser?.queueName;
  const [user, setUser] = useState<IEntity>(getUser());
  const [userRegion, setUserRegion] = useState('Unknown');
  const [counter, setCounter] = useState(session.timer.time);
  const [subPanel, setSubPanel] = useState<SubPanelType>(
    SipJs.conference ? 'groupCall' : 'note',
  );
  const audioRef = useRef<HTMLAudioElement>(null);

  const {
    callStore: {
      clearCallData,
      getCallControls,
      setCallControls,
      clearCallNote,
    },
  } = useStore();

  const { muted, paused, onHold } = getCallControls(session.id);
  const disablePauseRecordingFlag = useFeatureFlag(
    PAUSE_RECORDING_FEATURE_FLAG,
  );

  useEffect(() => {
    if (user.initialPhone !== COMMON.UNKNOWN_ENTITY.phone) {
      setUserRegion(getRegionByPhoneNumber(user.initialPhone));
    }
  }, [user]);

  useEffect(() => {
    if (session.status === 'active') {
      setUser(
        session.remoteUser.connectedUsers.length
          ? (session.remoteUser.connectedUsers[0] as IEntity)
          : ({
              ...COMMON.UNKNOWN_ENTITY,
            } as IEntity),
      );
      setCallControls(session.id, { onHold: false });
    } else {
      setCallControls(session.id, { onHold: true });
    }
  }, [session.status, session.remoteUser.connectedUsers]);

  const shouldShowTransfer = () => {
    return (
      session.remoteUser.type === USER_TYPE.EXTERNAL && !SipJs.isMultiSession()
    );
  };

  const shouldShowDialPad = () => {
    const isDynamicsV2 = Dynamics.getVersion() === 2;
    const isOtherSessionActive =
      SipJs.isMultiSession() &&
      session.id !== SipJs.getInvitedForTransferSession()?.id;

    return !isDynamicsV2 && !isOtherSessionActive;
  };

  const shouldShowGroupCall = () => {
    return (
      session.remoteUser.type === USER_TYPE.EXTERNAL && !SipJs.isMultiSession()
    );
  };

  const toggleMuteCall = () => {
    if (muted) {
      SipJs.unMuteCall(session.id);
    } else {
      SipJs.muteCall(session.id);
    }
    setCallControls(session.id, { muted: !muted });
  };

  const toggleHoldCall = () => {
    if (onHold) {
      SipJs.unHoldCall(session.id);
    } else {
      SipJs.holdCall(session.id);
    }
    setCallControls(session.id, { onHold: !onHold });
  };

  const switchSubPanel = (panel: SubPanelType) => {
    setSubPanel(panel);
    onSwitchSubPanel(panel);
  };

  const declineCall = () => {
    clearCallData();
    const { wrapUpQueue } = State.state$.getValue();
    if (!wrapUpQueue?.time) {
      clearCallNote();
    }
    SipJs.declineCall(session.id);
  };

  const togglePauseRecording = () => {
    const ownExtensionId = getExtensionFromUri(SipJs.authData.uri);
    SipJs.pauseOrResumeCall(ownExtensionId, session.xSessionId, !paused);
    setCallControls(session.id, { paused: !paused });
  };

  useEffect(() => {
    const sub$ = session.stream$.subscribe(({ status, stream }: IStream) => {
      if (status === 'addstream') {
        setCallControls(session.id, { onHold: false });
        audioRef.current.srcObject = stream;
        audioRef.current.play();
        return;
      }

      if (status === 'removestream') {
        setCallControls(session.id, { onHold: true });
        audioRef.current.pause();
      }
    });

    return () => {
      return sub$.unsubscribe();
    };
  }, [session]);

  useEffect(() => {
    const sub$ = session.timer.timeState$.subscribe((time: string) => {
      setCounter(time);
    });

    return () => {
      return sub$.unsubscribe();
    };
  }, [session]);

  const controls = useMemo(
    () => (
      <>
        <Tooltip
          placement="top"
          title={muted ? 'Unmute a call' : 'Mute a call'}
        >
          <Button
            type="text"
            data-testid={muted ? 'unmute-call' : 'mute-call'}
            onClick={toggleMuteCall}
            icon={<MessageIcon svgUrl={muted ? unmuteIcon : muteIcon} />}
          />
        </Tooltip>
        {!disablePauseRecordingFlag.isEnabled && (
          <Tooltip
            placement="top"
            title={paused ? 'Continue a recording' : 'Pause a recording'}
          >
            <Button
              type="text"
              data-testid={paused ? 'resume-recording' : 'pause-recording'}
              onClick={togglePauseRecording}
              icon={
                <MessageIcon
                  svgUrl={paused ? unPauseRecordingIcon : pauseRecordingIcon}
                />
              }
            />
          </Tooltip>
        )}
        <Tooltip
          placement="top"
          title={onHold ? 'Resume a call' : 'Pause a call'}
        >
          <Button
            type="text"
            onClick={toggleHoldCall}
            data-testid={onHold ? 'resume-call' : 'pause-call'}
            disabled={
              subPanel === 'groupCall' && !!SipJs.conference?.conferenceId
            }
            icon={<MessageIcon svgUrl={onHold ? unHoldIcon : holdIcon} />}
          />
        </Tooltip>
        {shouldShowTransfer() && (
          <Tooltip placement="top" title="Transfer a call">
            <Button
              type="text"
              data-testid={
                subPanel === 'transfer'
                  ? 'switch-to-note'
                  : 'switch-to-transfer'
              }
              onClick={() =>
                switchSubPanel(subPanel === 'transfer' ? 'note' : 'transfer')
              }
              disabled={
                subPanel === 'groupCall' && !!SipJs.conference?.conferenceId
              }
              icon={
                <MessageIcon
                  svgUrl={
                    subPanel === 'transfer' ? transferClickedIcon : transferIcon
                  }
                />
              }
            />
          </Tooltip>
        )}
        {shouldShowDialPad() && (
          <Tooltip placement="top" title="Numpad">
            <Button
              type="text"
              data-testid={
                subPanel === 'keyPad' ? 'switch-to-note' : 'switch-to-keypad'
              }
              onClick={() =>
                switchSubPanel(subPanel === 'keyPad' ? 'note' : 'keyPad')
              }
              icon={
                <MessageIcon
                  svgUrl={
                    subPanel === 'keyPad' ? dialPadIconClicked : dialPadIcon
                  }
                />
              }
            />
          </Tooltip>
        )}
        {shouldShowGroupCall() && (
          <Tooltip placement="top" title="Group call">
            <Button
              type="text"
              data-testid={
                subPanel === 'groupCall'
                  ? 'switch-to-note'
                  : 'switch-to-groupCall'
              }
              onClick={() =>
                switchSubPanel(subPanel === 'groupCall' ? 'note' : 'groupCall')
              }
              icon={
                <MessageIcon
                  svgUrl={
                    subPanel === 'groupCall'
                      ? groupCallClickedIcon
                      : groupCallIcon
                  }
                />
              }
            />
          </Tooltip>
        )}
      </>
    ),
    [muted, paused, onHold, subPanel],
  );

  return (
    <div className={`player player--${session.status}`}>
      <div className="header">
        <div className="header__info">
          <AccountInfo
            entity={user.type}
            id={user.id}
            size={40}
            fullName={user.fullName}
            phone={user.initialPhone}
          />
          {session.remoteUser.type === USER_TYPE.EXTERNAL && (
            <div className="location back-text">{userRegion}</div>
          )}
        </div>
        <div className="header__audio-recorder">
          <div className="timer" onClick={togglePauseRecording}>
            <div>{counter}</div>
            <Badge color="red" />
          </div>
        </div>
      </div>
      {queueName && (
        <div className="queue">{`Incoming call via ${queueName}`}</div>
      )}
      <Divider className="divider" />
      <div className="controls">
        {controls}
        <Button
          danger
          className="call-button"
          data-testid="decline-call"
          type="primary"
          shape="circle"
          onClick={declineCall}
          icon={<MessageIcon svgUrl={declineIcon} />}
        />
      </div>
      <audio ref={audioRef} className="audio-stream" src=""></audio>
    </div>
  );
});
